import type { QixitAuthSuccess } from '@shared/auth/types';
import apiService from './api';
import { isMicropayScopedToken, setSharedAuthCookie } from './cookieAuth';
import { promoteCheckoutToken } from './adoptSharedSession';
import { mapLoginResponseToUser } from '../store/authSlice';
import { writeLastActivityAt } from '../utils/sessionInactivity';
import { isBiometricUnlockEnabled, markBiometricSessionUnlocked } from '../utils/biometricUnlock';
import type { LoginResponse, User } from '../types';

/**
 * Stores a session produced by the shared auth panel. Express signup mints a
 * checkout-scoped token, which is promoted to a full mailbox session first.
 */
export async function finishMailboxSignIn(result: QixitAuthSuccess): Promise<User> {
  let accessToken = result.token;
  let refreshToken = result.refreshToken || '';
  if (isMicropayScopedToken(accessToken)) {
    const promoted = await promoteCheckoutToken(accessToken);
    if (!promoted) throw new Error('Please sign in to Mailbox to continue. Accounts with two-step verification must confirm it here.');
    accessToken = promoted.accessToken;
    refreshToken = promoted.refreshToken;
  }

  const raw = (result.raw as LoginResponse | undefined) ?? undefined;
  const response: LoginResponse = {
    ...(raw || {}),
    accessToken,
    refreshToken,
    userId: result.userId,
    username: raw?.username || result.username,
    loginHandle: raw?.loginHandle || result.username,
  } as LoginResponse;

  apiService.setTokens(accessToken, refreshToken);
  const user = mapLoginResponseToUser(response);
  localStorage.setItem('user', JSON.stringify(user));
  writeLastActivityAt();
  setSharedAuthCookie(result.userId, result.username, accessToken);
  if (isBiometricUnlockEnabled()) markBiometricSessionUnlocked();
  return user;
}

/** Only qixit.com properties (and local dev hosts) are valid post-login destinations. */
export function isTrustedReturnUrl(returnUrl: string): boolean {
  const value = returnUrl.trim();
  if (!value) return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const local = host === 'localhost' || host === '127.0.0.1';
    if (!local && url.protocol !== 'https:') return false;
    return local || host === 'qixit.com' || host.endsWith('.qixit.com');
  } catch {
    return false;
  }
}
